import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Pin, PinOff, ImageOff } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { EnrichedToothImage } from '@/hooks/useToothImages';
import { ImageLightbox } from './ImageLightbox';
import { useDentalOverlay } from './DentalOverlayContext';

// ── Helpers ────────────────────────────────────────────────────────────────────

function groupByDateAndKind(images: EnrichedToothImage[]) {
  const groups = new Map<string, EnrichedToothImage[]>();

  for (const img of images) {
    const date = new Date(img.acquired_at).toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const key = `${date}__${img.image_kind}`;
    const existing = groups.get(key) ?? [];
    groups.set(key, [...existing, img]);
  }

  return Array.from(groups.entries()).map(([key, imgs]) => {
    const [date] = key.split('__');
    return { date, kind: imgs[0].image_kind, images: imgs };
  });
}

function kindLabel(kind: string, t: (k: string) => string) {
  return t(`dental.imaging.kinds.${kind}`);
}

// ── Image card ────────────────────────────────────────────────────────────────

interface ImageCardProps {
  image: EnrichedToothImage;
  onClick: () => void;
  isPinned: boolean;
  onPinToggle: () => void;
}

function ImageCard({ image, onClick, isPinned, onPinToggle }: ImageCardProps) {
  const { t } = useTranslation();

  return (
    <div className="group relative overflow-hidden rounded-xl border border-ds-line bg-ds-card shadow-card transition-all hover:border-ds-teal-line hover:shadow-md">
      {/* Thumbnail */}
      <button onClick={onClick} className="block h-36 w-full overflow-hidden bg-ds-neutral-tint bg-ds-sunk">
        {image.signedUrl ? (
          <img
            src={image.signedUrl}
            alt={image.document_original_filename ?? 'tooth image'}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <ImageOff className="h-8 w-8 text-ds-placeholder" />
          </div>
        )}
      </button>

      {/* Footer */}
      <div className="flex items-center gap-1 px-2 py-1.5">
        <Camera className="h-3.5 w-3.5 flex-shrink-0 text-ds-teal" />
        <div className="min-w-0 flex-1">
          {image.tooth_fdis.length > 0 && (
            <div className="flex flex-wrap gap-0.5">
              {image.tooth_fdis.map((fdi) => (
                <span
                  key={fdi}
                  className="rounded-full bg-ds-neutral-tint px-1.5 py-0.5 text-xs text-ds-muted bg-ds-sunk"
                >
                  {fdi}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Pin button */}
        <button
          onClick={(e) => { e.stopPropagation(); onPinToggle(); }}
          title={isPinned ? t('dental.imaging.gallery.unpinOverlay') : t('dental.imaging.gallery.pinOverlay')}
          className={cn(
            'rounded-lg p-1 transition-colors',
            isPinned
              ? 'text-ds-teal hover:text-ds-teal'
              : 'text-ds-placeholder hover:text-ds-teal',
          )}
        >
          {isPinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

// ── Group header ──────────────────────────────────────────────────────────────

function GroupHeader({ date, kind, count, t }: { date: string; kind: string; count: number; t: (k: string) => string }) {
  return (
    <div className="flex items-center gap-2 pb-2 pt-4 first:pt-0">
      <span className="text-sm font-semibold text-ds-text">
        {kindLabel(kind, t)} {count > 1 ? `${count}-stk` : ''}
      </span>
      <span className="text-sm text-ds-muted">— {date}</span>
      <div className="flex-1 border-t border-ds-line" />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface ImageGalleryProps {
  images: EnrichedToothImage[];
  patientId: string;
  userId: string;
  filterToothFdi?: string | null;
}

export function ImageGallery({ images, patientId, userId, filterToothFdi }: ImageGalleryProps) {
  const { t } = useTranslation();
  const { pinnedImage, setPinnedImage, setOpacity } = useDentalOverlay();

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filteredImages = useMemo(
    () =>
      filterToothFdi
        ? images.filter((img) => img.tooth_fdis.includes(filterToothFdi))
        : images,
    [images, filterToothFdi],
  );

  const groups = useMemo(() => groupByDateAndKind(filteredImages), [filteredImages]);

  const handlePinToggle = (img: EnrichedToothImage) => {
    if (pinnedImage?.id === img.id) {
      setPinnedImage(null);
    } else {
      setPinnedImage(img);
      setOpacity(0.5);
    }
  };

  if (filteredImages.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-ds-line bg-ds-sunk p-8 bg-ds-card/50">
        <div className="text-center">
          <Camera className="mx-auto h-8 w-8 text-ds-placeholder" />
          <p className="mt-2 text-sm text-ds-muted">
            {t('dental.imaging.gallery.empty')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {groups.map((group) => (
        <div key={`${group.date}__${group.kind}`}>
          <GroupHeader date={group.date} kind={group.kind} count={group.images.length} t={t} />
          <div className="grid grid-cols-3 gap-3 xl:grid-cols-4">
            {group.images.map((img) => {
              const globalIdx = filteredImages.indexOf(img);
              return (
                <ImageCard
                  key={img.id}
                  image={img}
                  onClick={() => setLightboxIndex(globalIdx)}
                  isPinned={pinnedImage?.id === img.id}
                  onPinToggle={() => handlePinToggle(img)}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={filteredImages}
          initialIndex={lightboxIndex}
          patientId={patientId}
          userId={userId}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
